import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { devError } from "../lib/log";
import HeroSection from "../components/HeroSection";
import MovieCarousel from "../components/MovieCarousel";
import HomeCategoryRail from "../components/HomeCategoryRail";
import HomeWatchOn from "../components/HomeWatchOn";
import HomeMindMusicMotivation from "../components/HomeMindMusicMotivation";

// v2.37 — Inline-pill sub-filter definitions for Home rails. Each entry maps
// a collection slug to the pills shown in the rail header (above the cards).
// Keeps Home.jsx readable and the home<->collection-page filters in sync.
const FAMILY_PILLS = [
  { slug: "all",       label: "All" },
  { slug: "movies",    label: "Movies" },
  { slug: "series",    label: "Series" },
  { slug: "classic",   label: "Classic" },
  { slug: "comedy",    label: "Comedy" },
  { slug: "emotional", label: "Emotional" },
];
const HORROR_PILLS = [
  { slug: "all",           label: "All" },
  { slug: "indian",        label: "Indian" },
  { slug: "international", label: "International" },
  { slug: "folk",          label: "Folk" },
  { slug: "movies",        label: "Movies" },
  { slug: "series",        label: "Series" },
];
const STANDUP_PILLS = [
  { slug: "all",           label: "All" },
  { slug: "indian",        label: "Indian" },
  { slug: "international", label: "International" },
];
import HomeAnimeRail from "../components/HomeAnimeRail";
import HomeCreatorsHero from "../components/HomeCreatorsHero";
import HomeLatestVideos from "../components/HomeLatestVideos";
import HomeLiveTV from "../components/HomeLiveTV";
import HomeAnticipated from "../components/HomeAnticipated";
import HomeClassicsRail from "../components/HomeClassicsRail";
import PwaInstallBanner from "../components/PwaInstallBanner";
import SignInNudge from "../components/SignInNudge";
import GenreBootstrap from "../components/GenreBootstrap";
import FilmiMascot from "../components/FilmiMascot";
import LanguageFilter from "../components/LanguageFilter";
import JumpToRail from "../components/JumpToRail";
import LazyMount from "../components/LazyMount";
import ScrollProgressChip from "../components/ScrollProgressChip";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import Seo from "../components/Seo";
import { readSwrCache, writeSwrCache } from "../lib/swrCache";
import HomeSocialWidget from "../components/HomeSocialWidget";
import HomeSportsLive from "../components/HomeSportsLive";

// v3.2 — SortTabs: "Top Rated" vs "Trending" — both same size.
// Trending inactive = transparent bg + animated gradient border only (catches attention, not "selected").
// Trending active = moving gradient fill. Top Rated = amber active/inactive.
function SortTabs({ value, onChange, testidPrefix }) {
  const base = "px-3 py-1 rounded-full text-[10px] font-bold tracking-wide transition-all";
  return (
    <div
      className="inline-flex items-center bg-white/[0.04] border border-white/10 rounded-full p-0.5 gap-0.5 shrink-0"
      data-testid={`${testidPrefix}-sort-tabs`}
    >
      <button
        type="button"
        onClick={() => onChange("trending")}
        data-testid={`${testidPrefix}-sort-trending`}
        className={`${base} ${value === "trending" ? "trending-pill-active" : "trending-pill-inactive"}`}
      >
        TRENDING
      </button>
      <button
        type="button"
        onClick={() => onChange("top_rated")}
        data-testid={`${testidPrefix}-sort-top-rated`}
        className={`${base} ${value === "top_rated" ? "bg-amber-500/25 text-amber-200" : "text-white/55 hover:text-white/80"}`}
      >
        {/* Shorter label on mobile to avoid wrapping beside section title */}
        <span className="sm:hidden">TOP</span>
        <span className="hidden sm:inline">TOP RATED</span>
      </button>
    </div>
  );
}

// Skeleton shimmer rail — shown while "Top Rated" data is loading.
// Matches approximate MovieCard proportions so layout doesn't jump.
function RailSkeleton({ count = 10 }) {
  return (
    <div className="flex gap-3 overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="shrink-0 rounded-xl overflow-hidden"
          style={{ width: 140, animationDelay: `${i * 60}ms` }}
        >
          {/* Poster */}
          <div className="bg-white/[0.06] rounded-xl animate-pulse" style={{ width: 140, height: 210 }} />
          {/* Title line */}
          <div className="mt-2 mx-1 h-2.5 rounded-full bg-white/[0.06] animate-pulse" style={{ width: "75%", animationDelay: `${i * 60 + 80}ms` }} />
          {/* Sub line */}
          <div className="mt-1.5 mx-1 h-2 rounded-full bg-white/[0.04] animate-pulse" style={{ width: "50%", animationDelay: `${i * 60 + 160}ms` }} />
        </div>
      ))}
    </div>
  );
}

// Legacy ScopeTabs kept for Coming Soon rail (All / India / Global)
function ScopeTabs({ value, onChange, testidPrefix }) {
  const tabs = [
    { slug: "all",    label: "All" },
    { slug: "india",  label: "India" },
    { slug: "global", label: "Global" },
  ];
  return (
    <div
      className="inline-flex items-center bg-white/[0.04] border border-white/10 rounded-full p-0.5"
      data-testid={`${testidPrefix}-scope-tabs`}
    >
      {tabs.map((t) => (
        <button
          key={t.slug}
          type="button"
          onClick={() => onChange(t.slug)}
          data-testid={`${testidPrefix}-scope-${t.slug}`}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide transition ${
            value === t.slug
              ? "bg-amber-500/25 text-amber-200"
              : "text-white/55 hover:text-white/80"
          }`}
        >
          {t.label.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

const GENRE_TILES = [
  { name: "Thriller", id: 53, color: "from-red-900/40 to-black" },
  { name: "Drama", id: 18, color: "from-amber-900/40 to-black" },
  { name: "Sci-Fi", id: 878, color: "from-blue-900/40 to-black" },
  { name: "Action", id: 28, color: "from-orange-900/40 to-black" },
  { name: "Romance", id: 10749, color: "from-pink-900/40 to-black" },
  { name: "Comedy", id: 35, color: "from-yellow-900/30 to-black" },
  { name: "Mystery", id: 9648, color: "from-purple-900/40 to-black" },
  { name: "Horror", id: 27, color: "from-zinc-900 to-black" },
];

const LANGUAGE_TILES = [
  { name: "Hindi", code: "hi" },
  { name: "Tamil", code: "ta" },
  { name: "Telugu", code: "te" },
  { name: "Malayalam", code: "ml" },
  { name: "Kannada", code: "kn" },
  { name: "English", code: "en" },
  { name: "Korean", code: "ko" },
  { name: "Japanese", code: "ja" },
];

export default function Home() {
  const { user } = useAuth();
  const [languages, setLanguages] = useState([]);
  // v2.17 — Stale-while-revalidate hydration. On revisit, the previous
  // session's home-bundle paints INSTANTLY from localStorage. Fresh data
  // overwrites it ~200-500ms later. This is the Netflix/Hotstar trick that
  // makes the app feel "already running" when reopened.
  const cachedBundle = readSwrCache("home-bundle:default") || {};
  const cachedCreatorsHero = readSwrCache("creators-home-hero") || null;
  const cachedClassics = readSwrCache("indian-classical-hits") || [];
  const cachedModernClassics = readSwrCache("indian-modern-classics") || [];
  const cachedDesiOriginals = readSwrCache("desi-originals") || [];
  const cachedRegionalMasters = readSwrCache("regional-masterpieces") || [];
  const cachedAnimeRail = readSwrCache("anime-rail") || [];

  const [india, setIndia] = useState(cachedBundle.trending_india || []);
  // v3.2 — New on OTT: sort tabs (Trending / Top Rated).
  // Default = "trending". Switching to Top Rated re-fetches sorted by Filmistani score.
  const [newOnOtt, setNewOnOtt] = useState([]);
  const [newOnOttSort, setNewOnOttSort] = useState("trending");
  const [ottSortLoading, setOttSortLoading] = useState(false);
  const ottTopRatedCache = useRef(readSwrCache('new-on-ott-top-rated') || null); // SWR-persisted across page loads
  // v3.2 — Now in Theatres: sort tabs (Trending / Top Rated).
  const [nowInTheatres, setNowInTheatres] = useState([]);
  const [theatresSort, setTheatresSort] = useState("trending");
  const [theatresSortLoading, setTheatresSortLoading] = useState(false);
  const theatresTopRatedCache = useRef(readSwrCache('now-in-theatres-top-rated') || null); // SWR-persisted across page loads
  // Refs track current sort so bundle-fetch async callbacks don't overwrite "top_rated" data.
  const newOnOttSortRef = useRef("trending");
  const theatresSortRef = useRef("trending");

  // Keep refs in sync with sort state changes
  const handleNewOnOttSortChange = useCallback((s) => { newOnOttSortRef.current = s; setNewOnOttSort(s); }, []);
  const handleTheatresSortChange = useCallback((s) => { theatresSortRef.current = s; setTheatresSort(s); }, []);
  const [global, setGlobal] = useState(cachedBundle.trending_global || []);
  const [topRated, setTopRated] = useState(cachedBundle.top_rated || []);
  const [hidden, setHidden] = useState(cachedBundle.hidden_gems || []);
  const [masters, setMasters] = useState(cachedBundle.masterpieces || []);
  const [collections, setCollections] = useState(() => readSwrCache("collections-bundle") || {});
  const [topMoviesAT, setTopMoviesAT] = useState(cachedBundle.top_movies_alltime || []);
  const [topSeriesAT, setTopSeriesAT] = useState(cachedBundle.top_series_alltime || []);
  // v2.95 — Top 50 Movies / Series Trending Now rails removed from Home per
  // user. The `top_*_trending` data is still in home-bundle (Search + TopList
  // pages need it), Home just doesn't render them.
  const [indianSeries, setIndianSeries] = useState(cachedBundle.indian_series || []);
  const [intlSeries, setIntlSeries] = useState(cachedBundle.intl_series || []);
  const [topMoviesByLang, setTopMoviesByLang] = useState([]);
  const [weekend, setWeekend] = useState(cachedBundle.weekend_binge || []);
  const [editorPicks, setEditorPicks] = useState(cachedBundle.editor_picks || []);
  const [anticipated, setAnticipated] = useState(cachedBundle.anticipated_india || []);
  const [anticipatedScope, setAnticipatedScope] = useState("india");
  const [classics, setClassics] = useState(cachedClassics);
  const [modernClassics, setModernClassics] = useState(cachedModernClassics);
  const [desiOriginals, setDesiOriginals] = useState(cachedDesiOriginals);
  const [regionalMasters, setRegionalMasters] = useState(cachedRegionalMasters);
  const [animeRail, setAnimeRail] = useState(cachedAnimeRail);
  // NEW: per-user personalization state
  const [refreshSeed, setRefreshSeed] = useState(0);     // bumped by ↻ Shuffle button
  const [creatorsHero, setCreatorsHero] = useState(cachedCreatorsHero); // new home hero data
  const [bootstrapOpen, setBootstrapOpen] = useState(false); // gated by user action
  // "Because you clicked X" — hyper-personalized rail anchored on user's
  // most-clicked title. Only fetched for signed-in users; null until loaded
  // or if user has <5 clicks (cold start).
  const [becauseRail, setBecauseRail] = useState(null);
  // historyRail removed — "Last Opened" rail removed per user feedback
  // L3 "For You" rail — full hyper-personalization (watchlist + clicks +
  // bootstrap + watched). Wider signal than "Because you clicked X".
  const [forYouRail, setForYouRail] = useState(null);
  // Published custom rails created in admin — fetched separately so they
  // never block the main home-bundle load.
  const [customRailMeta, setCustomRailMeta]   = useState([]);   // [{key, label, sort_order}]
  const [customRailData, setCustomRailData]   = useState({});   // {key: [items]}
  // Full pre-cap pool sizes from home-bundle, used by "See all X titles" tile.
  const [bundleTotals, setBundleTotals] = useState({});
  // Section visibility config — fetched once on mount
  const [sectionVis, setSectionVis] = useState({});

  // Helper: returns true if section is visible (default: true when config not loaded yet)
  const secVisible = (id) => sectionVis[id] !== false;

  useEffect(() => {
    (async () => {
      try {
        // For multi-select languages, we pipe them. Backend accepts single language; if multi, send first or omit
        const langParam = languages.length === 1 ? { language: languages[0] } : {};
        const langPipe = languages.length > 1 ? languages.join("|") : null;
        const params = { ...langParam };
        if (refreshSeed > 0) params.refresh = refreshSeed;

        const filt = (arr) => {
          const list = arr || [];
          if (languages.length === 0) return list;
          return list.filter((x) => languages.includes(x.original_language));
        };

        // v3.23 — Two-phase loading strategy.
        // Phase 1: Fire priority bundle (~8 KB / ~80ms) first so Coming Soon,
        //          New on OTT, Now in Theatres and For You paint immediately
        //          before the full 240 KB bundle arrives.
        // Phase 2: Full bundle fires concurrently; updates all rails when done.
        // Both calls run at the same time — the priority bundle just resolves
        // faster and gives React something to paint immediately.

        const priorityPromise = api.get("/home-priority-bundle", { params }).then(r => {
          const d = r.data || {};
          setAnticipated(filt(d.anticipated_india));
          // Only update OTT/Theatres from bundle if user is on Trending tab
          if (newOnOttSortRef.current === "trending") setNewOnOtt(filt(d.new_on_ott_this_week));
          if (theatresSortRef.current === "trending") setNowInTheatres(filt(d.now_in_theatres));
          setEditorPicks(filt(d.editor_picks));
          // B — trending rails now in priority bundle for faster first paint
          if (d.trending_india?.length) setIndia(filt(d.trending_india));
          if (d.trending_global?.length) setGlobal(filt(d.trending_global));
        }).catch(() => {}); // non-fatal — full bundle fills in below

        const fullPromise = api.get("/home-bundle", { params }).then(r => {
          const data = r.data || {};
          // Override all rails (including the 4 priority ones with fresh data)
          setAnticipated(filt(data.anticipated_india));
          // Only update OTT/Theatres from bundle if user is on Trending tab
          if (newOnOttSortRef.current === "trending") setNewOnOtt(filt(data.new_on_ott_this_week));
          if (theatresSortRef.current === "trending") setNowInTheatres(filt(data.now_in_theatres));
          setIndia(filt(data.trending_india));
          setGlobal(filt(data.trending_global));
          setTopRated(filt(data.top_rated));
          setHidden(filt(data.hidden_gems));
          setMasters(filt(data.masterpieces));
          setTopMoviesAT(filt(data.top_movies_alltime));
          setTopSeriesAT(filt(data.top_series_alltime));
          setIndianSeries(filt(data.indian_series));
          setIntlSeries(filt(data.intl_series));
          setWeekend(filt(data.weekend_binge));
          setEditorPicks(filt(data.editor_picks));
          setClassics(filt(data.indian_classical_hits));
          setModernClassics(filt(data.indian_modern_classics));
          setDesiOriginals(filt(data.desi_originals));
          setRegionalMasters(filt(data.regional_masterpieces));
          setAnimeRail(filt(data.anime_rail));
          // Extract full pre-cap pool sizes for the "See all X titles" tile.
          if (data._rail_totals) setBundleTotals(data._rail_totals);
          // v2.17 — Persist for instant paint on next visit (SWR).
          if (languages.length === 0) {
            writeSwrCache("home-bundle:default", data);
            if ((data.indian_classical_hits || []).length) writeSwrCache("indian-classical-hits", data.indian_classical_hits);
            if ((data.indian_modern_classics || []).length) writeSwrCache("indian-modern-classics", data.indian_modern_classics);
            if ((data.desi_originals || []).length) writeSwrCache("desi-originals", data.desi_originals);
            if ((data.regional_masterpieces || []).length) writeSwrCache("regional-masterpieces", data.regional_masterpieces);
            if ((data.anime_rail || []).length) writeSwrCache("anime-rail", data.anime_rail);
          }
          // v3.34 — Prefetch "Top Rated" sort variants in the background so the
          // Trending ↔ Top Rated toggle is instant on first click.
          // Runs after the full bundle so it never competes with critical-path data.
          // v6.1 — Always refresh top_rated caches in background (true SWR).
          // Previously only fetched when cache was empty; this meant stale pre-deploy
          // data could never be evicted even after a backend fix was shipped.
          api.get("/new-on-ott-this-week", { params: { sort: "top_rated" } })
            .then(r => {
              const data = r.data?.results || r.data || [];
              if (!data.length) return;
              ottTopRatedCache.current = data;
              writeSwrCache('new-on-ott-top-rated', data);
              if (newOnOttSortRef.current === "top_rated") setNewOnOtt(data);
            })
            .catch(() => {});
          api.get("/now-in-theatres", { params: { sort: "top_rated" } })
            .then(r => {
              const data = r.data?.results || r.data || [];
              if (!data.length) return;
              theatresTopRatedCache.current = data;
              writeSwrCache('now-in-theatres-top-rated', data);
              if (theatresSortRef.current === "top_rated") setNowInTheatres(data);
            })
            .catch(() => {});
        }).catch((e) => { devError(e); });

        // Wait for both to settle (priority already painted above)
        await Promise.allSettled([priorityPromise, fullPromise]);
      } catch (e) {
        devError(e);
      }
    })();
  }, [languages, refreshSeed]);

  // v3.2 — Sort tab re-fetch effects for New on OTT and Now in Theatres.
  // "trending" = bundle default (buzz-sorted). "top_rated" = re-fetch with sort=top_rated (filmistani_score DESC).
  useEffect(() => {
    newOnOttSortRef.current = newOnOttSort;
    if (newOnOttSort === "trending") {
      // Restore bundle data (trending-sorted) when switching back to Trending tab
      const bundleData = readSwrCache("home-bundle:default");
      if (bundleData?.new_on_ott_this_week?.length) {
        setNewOnOtt(bundleData.new_on_ott_this_week);
      }
      return;
    }
    // "top_rated" — serve stale cache instantly (fast UX), then always
    // refresh in background so backend fixes are picked up immediately
    // without requiring a hard-refresh (true SWR, v6.1).
    if (ottTopRatedCache.current) {
      setNewOnOtt(ottTopRatedCache.current); // show stale instantly
      // fall through — background refresh below will update it
    }
    let alive = true;
    if (!ottTopRatedCache.current) setOttSortLoading(true);
    (async () => {
      try {
        const r = await api.get("/new-on-ott-this-week", { params: { sort: "top_rated" } });
        if (alive && newOnOttSortRef.current === "top_rated") {
          const results = r.data?.results || [];
          if (results.length) {
            ottTopRatedCache.current = results;
            writeSwrCache('new-on-ott-top-rated', results);
            setNewOnOtt(results);
          }
        }
      } catch (e) { devError(e); }
      finally { if (alive) setOttSortLoading(false); }
    })();
    return () => { alive = false; };
  }, [newOnOttSort]);

  useEffect(() => {
    theatresSortRef.current = theatresSort;
    if (theatresSort === "trending") {
      // Restore bundle data (trending-sorted) when switching back to Trending tab
      const bundleData = readSwrCache("home-bundle:default");
      if (bundleData?.now_in_theatres?.length) {
        setNowInTheatres(bundleData.now_in_theatres);
      }
      return;
    }
    // "top_rated" — serve stale cache instantly, always refresh in background (v6.1)
    if (theatresTopRatedCache.current) {
      setNowInTheatres(theatresTopRatedCache.current); // show stale instantly
      // fall through — background refresh below will update it
    }
    let alive = true;
    if (!theatresTopRatedCache.current) setTheatresSortLoading(true);
    (async () => {
      try {
        const r = await api.get("/now-in-theatres", { params: { sort: "top_rated" } });
        if (alive && theatresSortRef.current === "top_rated") {
          const results = r.data?.results || [];
          if (results.length) {
            theatresTopRatedCache.current = results;
            writeSwrCache('now-in-theatres-top-rated', results);
            setNowInTheatres(results);
          }
        }
      } catch (e) { devError(e); }
      finally { if (alive) setTheatresSortLoading(false); }
    })();
    return () => { alive = false; };
  }, [theatresSort]);

  useEffect(() => {
    // Always fetch /coming-soon on mount (regardless of scope) so the
    // sheet-curated entries (Jailer 2, Koffee with Karan, etc.) always replace
    // any stale bundle data. The bundle's anticipated_india field may be cached
    // on an old cache key that pre-dates sheet-first mode.
    let alive = true;
    (async () => {
      try {
        const params = anticipatedScope !== "india" ? { scope: anticipatedScope } : {};
        const r = await api.get("/coming-soon", { params });
        if (alive && (r.data?.results || []).length) {
          setAnticipated(r.data.results);
        }
      } catch (e) { devError(e); }
    })();
    return () => { alive = false; };
  }, [anticipatedScope]);

  // v4.0 — Secondary bundle: replaces 3 separate always-on-mount calls
  // (creators/home-hero, section-config, published-custom-rails + N rail items).
  // Saves 3+ HTTP round-trips; 5-min CDN cache + stale-while-revalidate.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/home-secondary-bundle");
        if (!alive) return;
        const d = r.data || {};

        // creators hero
        if (d.home_hero) {
          setCreatorsHero(d.home_hero);
          writeSwrCache("creators-home-hero", d.home_hero);
        }

        // section visibility config
        setSectionVis(d.section_config || {});

        // custom rails
        if (d.custom_rail_meta?.length) {
          setCustomRailMeta(d.custom_rail_meta);
          setCustomRailData(d.custom_rail_data || {});
        }
      } catch (e) {
        devError(e);
        setSectionVis({}); // fail-open
      }
    })();
    return () => { alive = false; };
  }, []);

  // v2.97z — The 5 useEffects that previously fetched Indian Classical Hits,
  // Indian Modern Classics, Desi Originals, Regional Masterpieces, and
  // Anime Rail individually have been folded into the /home-bundle response.
  // The 5 hand-curated SWR cache keys (indian-classical-hits, etc.) are still
  // written by the bundle setter above so legacy first-paint hydration code
  // keeps working — but no separate HTTP round-trips fire on each page load.

  // Fetch "Because you clicked X" rail — only for signed-in users with click history.
  // Re-fetched on refreshSeed change so the ↻ Shuffle button refreshes this too.
  // "Last Opened" history fetch removed — rail removed per user feedback

  useEffect(() => {
    if (!user) { setBecauseRail(null); return; }
    (async () => {
      try {
        const r = await api.get("/me/because-you-clicked");
        const data = r.data || {};
        if (data.anchor && (data.results || []).length >= 4) {
          setBecauseRail(data);
        } else {
          setBecauseRail(null);
        }
      } catch (e) {
        setBecauseRail(null);
      }
    })();
  }, [user, refreshSeed]);

  // Fetch L3 "For You" rail — full hyper-personalization. Rendered below
  // Trending Globally (so users see fresh trending content first, then their
  // personal picks). 65% global + 35% Indian content mix.
  // rail genuinely feels "fresh" without the user having to click Shuffle.
  // We keep the load-counter in localStorage (persists across sessions);
  // every 3rd load, we mint a new seed. Within a logical session, the seed
  // stays stable so we don't reshuffle on tab focus / nav back.
  useEffect(() => {
    (async () => {
      try {
        let loadCount = parseInt(localStorage.getItem("fm_foryou_loads") || "0", 10) + 1;
        try { localStorage.setItem("fm_foryou_loads", String(loadCount)); } catch (e) { /* private mode */ }
        let sessionSeed = sessionStorage.getItem("fm_foryou_seed");
        // Mint a new seed on first session-load AND every 3rd page-load globally.
        if (!sessionSeed || loadCount % 3 === 0) {
          sessionSeed = String(Math.floor(Math.random() * 100000) + 1);
          try { sessionStorage.setItem("fm_foryou_seed", sessionSeed); } catch (e) { /* private mode */ }
        }
        const effectiveRefresh = refreshSeed ? String(refreshSeed) : sessionSeed;
        const r = await api.get("/me/for-you", { params: { refresh: effectiveRefresh } });
        const data = r.data || {};
        const all = data.results || [];
        if (all.length >= 6) {
          setForYouRail({ ...data, results: all.slice(0, 80), buffer: all.slice(80) });
        } else {
          setForYouRail(null);
        }
      } catch (e) {
        setForYouRail(null);
      }
    })();
  }, [user, refreshSeed]);

  // v1.73 card-level swap: when user clicks "Show less" on a specific card,
  // remove ONLY that card and slide in a buffered replacement (instead of
  // shuffling the whole rail). Genre pref is still saved in backend so
  // future visits apply the broader filter.
  useEffect(() => {
    const onShowLessCard = (e) => {
      const tid = e.detail?.tmdb_id;
      const mt = e.detail?.media_type;
      if (!tid) return;
      setForYouRail((rail) => {
        if (!rail) return rail;
        const filtered = (rail.results || []).filter(
          (r) => !(r.tmdb_id === tid && r.media_type === mt)
        );
        const buffer = rail.buffer || [];
        // Slide in next buffered item that ISN'T already visible
        let replacement = null;
        const visibleIds = new Set(filtered.map((r) => `${r.media_type}:${r.tmdb_id}`));
        const remainingBuffer = [];
        for (const b of buffer) {
          if (!replacement && !visibleIds.has(`${b.media_type}:${b.tmdb_id}`)) {
            replacement = b;
          } else {
            remainingBuffer.push(b);
          }
        }
        const nextResults = replacement ? [...filtered, replacement] : filtered;
        return { ...rail, results: nextResults, buffer: remainingBuffer };
      });
      setBecauseRail((rail) => {
        if (!rail) return rail;
        const filtered = (rail.results || []).filter(
          (r) => !(r.tmdb_id === tid && r.media_type === mt)
        );
        return { ...rail, results: filtered };
      });
    };
    window.addEventListener("fm-show-less-card", onShowLessCard);
    return () => window.removeEventListener("fm-show-less-card", onShowLessCard);
  }, []);

  // Soft refresh For-You when user adjusts show-less preferences AND the
  // local buffer ran out — only as a fallback to ensure the genre filter is
  // applied for the rest of the session.
  useEffect(() => {
    const onShowLessUpdated = (e) => {
      // If buffer is empty (or this is a manual reset), do a soft refresh
      const bufferEmpty = e.detail?.bufferEmpty === true;
      if (bufferEmpty) {
        setRefreshSeed((s) => (s || 0) + 1);
      }
    };
    window.addEventListener("fm-show-less-updated", onShowLessUpdated);
    return () => window.removeEventListener("fm-show-less-updated", onShowLessUpdated);
  }, []);

  // ↻ Shuffle handler — bumps refresh seed → backend reshuffles personalized rails
  const onShuffle = useCallback(async () => {
    setRefreshSeed((s) => s + 1);
    // If signed in + no taste data yet, surface the Genre Bootstrap card
    // contextually (one-time, dismissible). Cold-start fix.
    if (user) {
      try {
        const done = localStorage.getItem("fm_bootstrap_done") === "1";
        if (!done) {
          const r = await api.get("/me/prefs");
          const hasBootstrap = !!r.data?.has_bootstrap;
          const hasWatchlist = (r.data?.hidden_titles?.length || 0) > 0; // light proxy
          if (!hasBootstrap && !hasWatchlist) {
            setBootstrapOpen(true);
          }
        }
      } catch (e) {
        // ignore — bootstrap is optional UX nicety
      }
    }
  }, [user]);

  const onBootstrapSaved = useCallback(() => {
    setBootstrapOpen(false);
    setRefreshSeed((s) => s + 1); // re-fetch with new taste vector
  }, []);

  // Collections (16 thematic rails) — fetched in parallel with the home-bundle
  // so all rails paint at roughly the same time. Previously we deferred this
  // by 800ms which caused the "centre cards taking forever" perception.
  useEffect(() => {
    (async () => {
      try {
        // v2.30 — Curated themed collections (Marvel, K-Drama, Ghibli, etc.)
        // are intentionally NOT filtered by language unless 2+ langs picked.
        // The user expects "K-Drama" rail to still show when they pick Hindi.
        const langPipe = languages.length > 1 ? languages.join("|") : null;
        const filt = (arr) => {
          const list = arr || [];
          if (languages.length === 0) return list;
          return list.filter((x) => languages.includes(x.original_language));
        };
        // Engagement-first sequence: mainstream/nostalgic franchises first,
        // then mood/depth, then long-tail documentaries.
        const featured = [
          "all-time-hit-series",
          "marvel-magic",
          "k-drama-obsession",
          "ghibli-magic",
          "fantasy-worlds",
          "cartoon-network-classics",
          "horror-hits",
          "family-favourites",
          "comedy-hits",
          "feel-good",
          "high-octane-action",
          "survival-thrillers",
          "mind-bending-thrillers",
          "intelligent-scifi",
          "emotional-masterpieces",
          "romantic-films",
          "motivational-films",
          "corporate-startup-stories",
          "stand-up-comedy",
          "animation",
          "all-documentaries",
          // v2.32 — Nature / True-Crime / Sports docs collapsed into the
          // unified Documentaries rail (sub-pills inside the collection page).
          // The legacy collection URLs still work for backwards-compat.
        ];
        const r = await api.get(`/collections-bundle`, { params: { slugs: featured.join(","), sort: "score", slim: true } });
        const map = {};
        const data = r.data?.collections || {};
        featured.forEach((s) => {
          const c = data[s];
          if (c) map[s] = { ...c, results: filt(c.results || []) };
        });
        setCollections(map);
        // v2.19 — Persist for instant paint on revisit (language-agnostic key).
        if (languages.length === 0 && Object.keys(map).length > 0) {
          writeSwrCache("collections-bundle", map);
        }
      } catch (e) {
        devError(e);
      }
    })();
  }, [languages]);

  // v2.30 — Language filter expanded for "Hindi" picker. Per user spec
  // ("Indian Origin Content + Regional Indian dubbed in Hindi"), Hindi
  // selection now ALSO matches all Indian-origin languages (ta/te/ml/kn/
  // bn/mr/pa/gu/or/as) — these regional titles all get Hindi dubs widely
  // and are part of the same audience's habit set. Without this, the Hindi
  // rail shrinks to ~5 items.
  const INDIA_LANG_SET = ["hi", "ta", "te", "ml", "kn", "bn", "mr", "pa", "gu", "or", "as"];
  const expandLangs = (langs) => {
    const out = new Set(langs);
    if (langs.includes("hi")) INDIA_LANG_SET.forEach((l) => out.add(l));
    return out;
  };
  // v2.97t — Strict language filter. Used by ALL rails uniformly so when
  // the user clicks "Hindi" only Hindi-language titles appear across every
  // rail (Bandar, Brown, Pyramid Scheme, Dhurandhar — not English/Korean
  // titles). Empty rails simply disappear, matching user expectation.
  const langFilter = (arr) => {
    if (!arr || arr.length === 0) return arr;
    if (languages.length === 0) return arr;
    const set = expandLangs(languages);
    return arr.filter((x) => set.has(x.original_language));
  };

  // Top movies in user's selected language (if exactly one selected)
  useEffect(() => {
    if (languages.length !== 1) { setTopMoviesByLang([]); return; }
    (async () => {
      try {
        const r = await api.get("/top-imdb", { params: { media_type: "movie", scope: "all_time", language: languages[0], limit: 30 } });
        setTopMoviesByLang(r.data.results || []);
      } catch (e) { setTopMoviesByLang([]); }
    })();
  }, [languages]);

  return (
    <div className="bg-[#0A0A0A] min-h-screen pt-16" data-testid="home-page">
      <Seo
        title="Movie & Web Series Recommendations · Hindi, Tamil, Telugu, Korean & Global Cinema"
        description="Filmistani — find great cinema faster. Hand-picked movie & web series discovery with live IMDb ratings, curated rails (Marvel, Studio Ghibli, K-Drama, Cartoon Network, sitcoms), family-safe mode and Ask Filmi — your cinema buddy."
        path="/"
      />

      {/* 🎬 New on OTT — above hero for instant first-visit engagement */}
      {newOnOtt.length > 0 && (
        <section className="pt-4" data-testid="section-new-on-ott">
          <div className="px-6 lg:px-10 max-w-[1440px] mx-auto flex items-center justify-between mb-3 gap-2">
            <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight min-w-0 truncate">
              🎬 New on OTT
            </h2>
            <SortTabs
              value={newOnOttSort}
              onChange={handleNewOnOttSortChange}
              testidPrefix="new-on-ott"
            />
          </div>
          {ottSortLoading
            ? <div className="px-6 lg:px-10 max-w-[1440px] mx-auto"><RailSkeleton count={10} /></div>
            : <MovieCarousel
                title=""
                items={newOnOtt}
                testid="row-new-on-ott"
                showPlatform
                disableShuffle
                totalCount={bundleTotals.new_on_ott_this_week}
              />
          }
        </section>
      )}

      {/* 🍿 Now in Theatres — above hero */}
      {nowInTheatres.length > 0 && (
        <section className="mt-6" data-testid="section-now-in-theatres">
          <div className="px-6 lg:px-10 max-w-[1440px] mx-auto flex items-center justify-between mb-3 gap-2">
            <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight min-w-0 truncate">
              🍿 Now in Theatres
            </h2>
            <SortTabs
              value={theatresSort}
              onChange={handleTheatresSortChange}
              testidPrefix="now-in-theatres"
            />
          </div>
          {theatresSortLoading
            ? <div className="px-6 lg:px-10 max-w-[1440px] mx-auto"><RailSkeleton count={10} /></div>
            : <MovieCarousel
                title=""
                items={nowInTheatres}
                testid="row-now-in-theatres"
                showPlatform
                disableShuffle
                totalCount={bundleTotals.now_in_theatres}
              />
          }
        </section>
      )}

      {/* Coming Soon — moved above hero to complete "what's new & what's next" first-fold */}
      {secVisible("anticipated") && (
        <section className="mt-6" data-testid="section-coming-soon-wrapper">
          <div className="px-6 lg:px-10 max-w-[1440px] mx-auto flex items-center justify-end mb-2">
            <ScopeTabs
              value={anticipatedScope}
              onChange={setAnticipatedScope}
              testidPrefix="coming-soon"
            />
          </div>
          <HomeAnticipated initialItems={anticipated} />
        </section>
      )}

      <HeroSection />

      <LanguageFilter values={languages} onChange={setLanguages} />

      {/* v2.28 — Jump-to-Rail dropdown. Lets users skip the long home feed
          and anchor to any rail (mood, genre, OTT, top-rated, etc.). */}
      <JumpToRail />

      {/* v3.2 — Sort tabs: Trending (buzz-sorted) / Top Rated (filmistani_score DESC).
          Replaced old Most Recent toggle for New on OTT + Now in Theatres. */}

      {/* Sports LIVE — below Coming Soon. Cricket auto-fetches (India first),
          Football uses admin whitelist. Stale cache shown when API quota exceeded. */}
      {secVisible("sports_live") && (
        <LazyMount placeholderHeight={200}>
          <HomeSportsLive />
        </LazyMount>
      )}

      {/* Editor's Picks — sourced from a Google Sheet, refreshed daily.
          Sits high in the order so curated taste signals over algorithmic ones.
          Now personalized per-user with daily shuffle + ↻ button + 40-40-20 mix.
          v2.97z — Wrapped in LazyMount: this is the first rail below the
          fold on most desktop viewports, so deferring its imdb-bulk fetch
          + Pro-tier check until 200 px before scroll saves a network call. */}
      {(() => {
        const f = langFilter(editorPicks);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={320} testid="lazy-editor-picks">
            <MovieCarousel
              title="Filmistani Editor's Picks ✨"
              items={f}
              testid="row-editor-picks"
              viewAllHref="/list/editor-picks"
              onRefresh={onShuffle}
              showPlatform
              totalCount={bundleTotals.editor_picks}
            />
          </LazyMount>
        );
      })()}

      {/* Genre Bootstrap card — surfaced contextually after Shuffle when the
          signed-in user has zero taste signal. One-time, dismissible. */}
      {user && bootstrapOpen && (
        <GenreBootstrap onSaved={onBootstrapSaved} />
      )}

      {/* Sign-in nudge — only for guests, only once per 7 days. Sits directly
          under Editor's Picks where the personalization value-prop lives. */}
      {!user && <SignInNudge />}

      {/* 2. TRENDING — broad universal appeal, ranked by India-first relevance.
          v2.97z — Both rails wrapped in LazyMount (200 px rootMargin). */}
      <LazyMount placeholderHeight={320} testid="lazy-trending-india">
        <MovieCarousel title="Trending in India" items={langFilter(india)} testid="row-trending-india" viewAllHref="/list/trending-india" showPlatform totalCount={bundleTotals.trending_india} />
      </LazyMount>
      <LazyMount placeholderHeight={320} testid="lazy-trending-global">
        <MovieCarousel title="Trending Globally" items={langFilter(global)} testid="row-trending-global" viewAllHref="/list/trending-global" showPlatform totalCount={bundleTotals.trending_global} />
      </LazyMount>

      {/* L3 "For You" — Personalized rail below Trending. Built from all taste
          signals (watchlist + clicks + bootstrap). 65% global + 35% Indian mix.
          ↻ Shuffle gives a fresh order without re-querying TMDB.
          Hides for cold-start users (<6 picks) so they don't see an awkward stub. */}
      {(() => {
        const f = langFilter(forYouRail?.results || []);
        if (f.length < 6) return null;
        return (
          <LazyMount placeholderHeight={320} testid="lazy-for-you">
            <MovieCarousel
              title="For You ✨"
              items={f}
              testid="row-for-you"
              onRefresh={onShuffle}
              showLessVariant="inline"
              showPlatform
            />
          </LazyMount>
        );
      })()}

      {/* v2.41 — Mind, Music & Motivation rail. Sits ABOVE "Watch On" per
          user request — productive-community YouTube channels (Focus,
          Energy, Chill, Bhakti, Podcasts) curated from the user's sheet.
          Auto-shuffles every 25s for variety. */}
      {secVisible("mind_music") && (
        <LazyMount placeholderHeight={340}>
          <HomeMindMusicMotivation />
        </LazyMount>
      )}

      {/* v1.98 — "Watch On" rail. Colored platform pills let users filter
          to a single OTT (Netflix / Prime / JioHotstar / Sony LIV / ZEE5 /
          Apple TV+ / Amazon MX Player). Placed directly after Trending so the
          discovery loop is: see what's hot → narrow to my subscription. */}
      {secVisible("watch_on") && (
        <LazyMount placeholderHeight={340}>
          <HomeWatchOn />
        </LazyMount>
      )}
      {/* v2.16 — Below-fold rails lazy-mounted to cut first-paint API
          contention. Above-fold (For You, Recently, Anticipated, Editor's,
          Trending) stay eager. Each rail loads when scrolled within 600px. */}
      {(() => {
        const f = langFilter(desiOriginals);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={320} testid="lazy-desi-originals">
            <HomeCategoryRail
              slug="desi-originals"
              title="Desi Originals"
              initialItems={f}
              testid="row-desi-originals"
              totalCount={bundleTotals.desi_originals}
            />
          </LazyMount>
        );
      })()}
      {/* v2.95 — Top 50 Series/Movies Trending Now rails removed from Home
          (still available on TopList pages /top/tv/trending and
          /top/movies/trending via direct URL). */}

      {/* v2.22 — Corporate & Startup Stories pulled OUT of the thematic
          collections bundle and surfaced here, directly below Top 50 Movies.
          User wanted higher visibility for biopics, founder shows + reality
          (Shark Tank IN/Global, Dragons' Den, The Profit). */}
      {(() => {
        const f = langFilter(collections["corporate-startup-stories"]?.results || []);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={320} testid="lazy-corporate-startup-stories">
            <HomeCategoryRail
              slug="corporate-startup-stories"
              title={collections["corporate-startup-stories"].title}
              initialItems={f}
              testid="row-corporate-startup-stories"
              totalCount={collections["corporate-startup-stories"]?.total}
            />
          </LazyMount>
        );
      })()}

      {/* Creator Community hero — replaces the old single-row teaser with a
          richer 3-tile + bold CTA layout. The CTA tile draws more clicks to
          the /creators page where the full 150-creator catalog lives. Tiles
          rotate daily (deterministic by date). */}
      {secVisible("creators") && creatorsHero && creatorsHero.tiles?.length > 0 && (
        <LazyMount placeholderHeight={300}>
          <HomeCreatorsHero data={creatorsHero} />
        </LazyMount>
      )}

      {/* Latest Uploads from Creators — replaces the old Trending Creators
          rail. Shows 10 most-recent uploads from our 200+ creator network
          (one-per-channel, last 7 days). Self-fetches; renders nothing on
          empty so we never show a stale stub. */}
      {secVisible("latest_videos") && (
        <LazyMount placeholderHeight={340}>
          <HomeLatestVideos />
        </LazyMount>
      )}

      {/* Live TV — 24×7 LIVE news/business/music channels, circular avatars
          (Instagram-Stories style). Each tile opens YouTube live in a new
          tab. (v1.85) */}
      {secVisible("live_tv") && (
        <LazyMount placeholderHeight={200}>
          <HomeLiveTV />
        </LazyMount>
      )}

      {/* Sports LIVE rail moved to just below Trending — see above */}

      {/* PWA install nudge — subtle, dismissible, only for non-installed users.
          Renders nothing if already installed, dismissed recently, or
          beforeinstallprompt hasn't fired (Android/Chromium). iOS shows
          manual instructions. */}
      <PwaInstallBanner />

      {/* 3. HALL-OF-FAME — Masterpieces lead, then Top 50 fills in the next tier.
           Cross-rail dedup keeps each rail fresh (no more identical titles across rails).
           v2.16 — Entire block wrapped in LazyMount (way below the fold). */}
      <LazyMount placeholderHeight={960} testid="lazy-hall-of-fame">
      {(() => {
        const shown = new Set();
        const dedupe = (arr) => {
          const out = [];
          for (const it of arr) {
            const k = `${it.media_type}-${it.tmdb_id}`;
            if (shown.has(k)) continue;
            shown.add(k);
            out.push(it);
          }
          return out;
        };
        // Masterpieces FIRST — small elite club, gets visibility priority.
        // v2.0 — Use SOFT filter so picking a language doesn't make these
        // global-canon rails disappear silently.
        const mastersF = dedupe(langFilter(masters));
        const moviesATF = dedupe(langFilter(topMoviesAT));
        const seriesATF = dedupe(langFilter(topSeriesAT));
        return (
          <>
            {mastersF.length > 0 && (
              <MovieCarousel
                title="IMDb 9+ Masterpieces"
                items={mastersF}
                testid="row-imdb-masterpieces"
                viewAllHref="/list/imdb-masterpieces"
                showPlatform
                totalCount={bundleTotals.masterpieces}
              />
            )}
            {/* v2.38 — User request: Horror, Family, Documentaries rails
                positioned IMMEDIATELY below IMDb 9+ Masterpieces (was after
                Regional Masterpieces). High-engagement themed rails get
                premium real estate; filtered out of the bulk thematic .map
                below to prevent duplication. */}
            {collections["horror-hits"] && (() => {
              const f = langFilter(collections["horror-hits"].results || []);
              if (f.length === 0) return null;
              return (
                <HomeCategoryRail
                  slug="horror-hits"
                  title={collections["horror-hits"].title}
                  initialItems={f}
                  pills={HORROR_PILLS}
                  filterParam="horror_filter"
                  urlFilterParam="hf"
                  variant="horror"
                  totalCount={collections["horror-hits"]?.total}
                />
              );
            })()}
            {collections["family-favourites"] && (() => {
              const f = langFilter(collections["family-favourites"].results || []);
              if (f.length === 0) return null;
              return (
                <HomeCategoryRail
                  slug="family-favourites"
                  title={collections["family-favourites"].title}
                  initialItems={f}
                  pills={FAMILY_PILLS}
                  filterParam="family_filter"
                  urlFilterParam="ff"
                  totalCount={collections["family-favourites"]?.total}
                />
              );
            })()}
            {collections["all-documentaries"] && (() => {
              const f = langFilter(collections["all-documentaries"].results || []);
              if (f.length === 0) return null;
              return (
                <HomeCategoryRail
                  slug="all-documentaries"
                  title={collections["all-documentaries"].title}
                  initialItems={f}
                  fetchPillsUrl="/docs-categories"
                  filterParam="docs_category"
                  urlFilterParam="dc"
                  totalCount={collections["all-documentaries"]?.total}
                />
              );
            })()}
            {/* v2.48 — Animation rail with sub-pill bar (Disney & Pixar /
                DreamWorks / Marvel Animation / Cartoon Network / Indian /
                Kids & Family India / Mature & Dark / Superhero). 110+ titles.
                v2.51 — NEW badge removed (rail is settled in steady state). */}
            {collections["animation"] && collections["animation"].results?.length > 0 && (() => {
              const f = langFilter(collections["animation"].results || []);
              if (f.length === 0) return null;
              return (
                <HomeCategoryRail
                  slug="animation"
                  title={collections["animation"].title}
                  initialItems={f}
                  fetchPillsUrl="/animation-categories"
                  filterParam="animation_filter"
                  urlFilterParam="af"
                  totalCount={collections["animation"]?.total}
                />
              );
            })()}
            {/* v2.47 — Stand-Up Comedy rail (Indian + International). Same
                inline-pill UX as Family / Horror / Docs. */}
            {collections["stand-up-comedy"] && collections["stand-up-comedy"].results?.length > 0 && (() => {
              const f = langFilter(collections["stand-up-comedy"].results || []);
              if (f.length === 0) return null;
              return (
                <HomeCategoryRail
                  slug="stand-up-comedy"
                  title={collections["stand-up-comedy"].title}
                  initialItems={f}
                  pills={STANDUP_PILLS}
                  filterParam="standup_filter"
                  urlFilterParam="sf"
                  totalCount={collections["stand-up-comedy"]?.total}
                />
              );
            })()}
            {seriesATF.length > 0 && (
              <MovieCarousel
                title="Top 50 Series · All Time"
                items={seriesATF}
                testid="row-top-series-alltime"
                viewAllHref="/top/tv/all_time"
                showPlatform
                totalCount={bundleTotals.top_series_alltime}
              />
            )}
            {moviesATF.length > 0 && (
              <MovieCarousel
                title="Top 50 Movies · All Time"
                items={moviesATF}
                testid="row-top-movies-alltime"
                viewAllHref="/top/movies/all_time"
                showPlatform
                totalCount={bundleTotals.top_movies_alltime}
              />
            )}
          </>
        );
      })()}
      </LazyMount>

      {/* 4. REGIONAL — Indian + International series, language-aware top movies */}
      {langFilter(indianSeries).length > 0 && (
        <LazyMount placeholderHeight={320} testid="lazy-indian-series">
          <HomeCategoryRail
            slug="indian-series"
            title="Indian Web Series"
            initialItems={langFilter(indianSeries)}
            testid="row-indian-series"
            totalCount={bundleTotals.indian_series}
          />
        </LazyMount>
      )}
      {topMoviesByLang.length > 0 && languages.length === 1 && (
        <LazyMount placeholderHeight={320} testid="lazy-top-movies-by-lang">
        <MovieCarousel
          title={`Top Movies in ${(LANGUAGE_TILES.find((l) => l.code === languages[0]) || {}).name || languages[0].toUpperCase()}`}
          items={topMoviesByLang}
          testid="row-top-movies-by-lang"
          viewAllHref={`/discover?language=${languages[0]}`}
          showPlatform
        />
        </LazyMount>
      )}
      {intlSeries.length > 0 && (
        <LazyMount placeholderHeight={320} testid="lazy-intl-series">
          <HomeCategoryRail
            slug="international-series"
            title="International Series"
            initialItems={langFilter(intlSeries)}
            testid="row-intl-series"
            totalCount={bundleTotals.intl_series}
          />
        </LazyMount>
      )}

      {/* v1.99a — Dedicated Anime rail with minimal sub-genre filter pills
          (Action / Drama / Comedy / Romance / Sci-Fi / Mystery). Title kept
          short — "Anime" — and pills filter client-side from the cached
          ★8+ canon. */}
      {(() => {
        const f = langFilter(animeRail);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={340} testid="lazy-anime-rail">
            <HomeAnimeRail items={f} />
          </LazyMount>
        );
      })()}

      {/* Indian Classics — placed AFTER International Cinema per user direction.
          Decade tabs for the 50s-80s rail; companion 90s-2000s rail. */}
      {(() => {
        const f = langFilter(classics);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={360} testid="lazy-classics">
            <HomeClassicsRail items={f} />
          </LazyMount>
        );
      })()}
      {(() => {
        const f = langFilter(modernClassics);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={320} testid="lazy-modern-classics">
            <MovieCarousel
              title="Modern Indian Classics"
              items={f}
              testid="row-indian-modern-classics"
              viewAllHref="/list/indian-modern-classics"
              showPlatform
              totalCount={bundleTotals.indian_modern_classics}
            />
          </LazyMount>
        );
      })()}
      {/* v1.83 — Regional Masterpieces (★8+ Tamil/Telugu/Malayalam/Kannada/
          Bengali/Marathi) placed directly below Modern Indian Classics so
          the curated-canon block stays cohesive. */}
      {(() => {
        const f = langFilter(regionalMasters);
        if (f.length === 0) return null;
        return (
          <LazyMount placeholderHeight={320} testid="lazy-regional-masterpieces">
            <HomeCategoryRail
              slug="regional-masterpieces"
              title="Regional Masterpieces"
              initialItems={f}
              testid="row-regional-masterpieces"
              totalCount={bundleTotals.regional_masterpieces}
            />
          </LazyMount>
        );
      })()}

      {/* 5. THEMATIC COLLECTIONS — lazy-mounted; mainstream franchises first
           (Hit Series → Marvel → K-Drama → Ghibli → Cartoon Network), then mood,
           then long-tail documentaries.
           v2.0c — showPlatform on ALL themed rails. Marvel → JioHotstar,
           K-Drama → Netflix, Ghibli → Netflix, Feel-Good → Prime/Sony LIV
           chips render directly on the Home page (was previously only on
           the dedicated /collection/:slug page).
           v2.22 — `corporate-startup-stories` is rendered earlier (above) so
           we skip it here to avoid duplicating the rail. */}
      {/* v2.60 — All remaining genre rails now use HomeCategoryRail so every
           rail gets the TOP / LATEST sort toggle, matching Horror/Family/Docs. */}
      {Object.entries(collections)
        .filter(([slug]) => slug !== "corporate-startup-stories"
          // v2.38 — These 3 are rendered up-top right after IMDb 9+ Masterpieces.
          && slug !== "horror-hits"
          && slug !== "family-favourites"
          && slug !== "all-documentaries"
          // v2.47 — Stand-up is also up-top with its own inline-pill rail.
          && slug !== "stand-up-comedy"
          // v2.48 — Animation is also up-top.
          && slug !== "animation")
        .map(([slug, data]) => {
          const f = langFilter(data.results || []);
          if (f.length === 0) return null;
          return (
            <LazyMount key={slug} placeholderHeight={320} testid={`lazy-${slug}`}>
              <HomeCategoryRail
                slug={slug}
                title={data.title}
                initialItems={f}
                totalCount={data.total}
                testid={`row-${slug}`}
              />
            </LazyMount>
          );
        })}

      {/* 6. DEEPER DISCOVERY — Hidden gems & weekend rotation come last so users
           who scroll past the franchise rails get true off-the-radar picks. */}
      <LazyMount placeholderHeight={320} testid="lazy-hidden-gems">
        <MovieCarousel title="Hidden Gems" items={langFilter(hidden)} testid="row-hidden-gems" viewAllHref="/list/hidden-gems" showPlatform totalCount={bundleTotals.hidden_gems} />
      </LazyMount>
      {weekend.length > 0 && (
        <LazyMount placeholderHeight={320} testid="lazy-weekend-binge">
        <MovieCarousel
          title="Weekend Binge Picks"
          items={langFilter(weekend)}
          testid="row-weekend-binge"
          viewAllHref="/list/weekend-binge"
          collectionSlug="weekend-binge-picks"
          showPlatform
          totalCount={bundleTotals.weekend_binge}
        />
        </LazyMount>
      )}

      {/* Browse by Genre */}
      <section className="px-6 lg:px-10 py-12 max-w-[1440px] mx-auto" data-testid="browse-genre">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">Browse by Genre</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {GENRE_TILES.map((g) => (
            <Link key={g.id} to={`/discover?genre=${g.id}`}
                  data-testid={`genre-tile-${g.name.toLowerCase()}`}
                  className={`relative h-28 rounded-md overflow-hidden border border-white/10 bg-gradient-to-br ${g.color} flex items-end p-4 hover:border-white/30 transition-colors`}>
              <span className="font-display text-xl font-bold">{g.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Curated Worlds — quick-jump shortcuts to the new themed rails + Creators.
          Same Browse-by-Genre style so users who scroll past the long rail list
          still get one-tap access to every new category. */}
      <section className="px-6 lg:px-10 py-8 max-w-[1440px] mx-auto" data-testid="browse-curated-worlds">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">Curated Worlds</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { name: "Creator Community", href: "/creators", color: "from-[#FF0000]/30 to-[#FF0000]/10", testid: "world-creators" },
            { name: "All-Time Hit Series", href: "/collection/all-time-hit-series", color: "from-amber-700/30 to-amber-900/10", testid: "world-all-time-hit-series" },
            { name: "Marvel Magic", href: "/collection/marvel-magic", color: "from-red-700/30 to-red-900/10", testid: "world-marvel" },
            { name: "K-Drama Obsession", href: "/collection/k-drama-obsession", color: "from-pink-700/30 to-pink-900/10", testid: "world-k-drama" },
            { name: "Family Favourites", href: "/collection/family-favourites", color: "from-orange-700/30 to-orange-900/10", testid: "world-family-favourites" },
            { name: "For Ghibli Lovers", href: "/collection/ghibli-magic", color: "from-emerald-700/30 to-emerald-900/10", testid: "world-ghibli" },
            { name: "Fantasy Worlds", href: "/collection/fantasy-worlds", color: "from-indigo-700/30 to-indigo-900/10", testid: "world-fantasy" },
            { name: "Feel-Good Cinema", href: "/collection/feel-good", color: "from-yellow-600/30 to-yellow-900/10", testid: "world-feel-good" },
            { name: "Documentaries", href: "/collection/all-documentaries", color: "from-teal-700/30 to-teal-900/10", testid: "world-documentaries" },
            { name: "Horror Hits", href: "/collection/horror-hits", color: "from-rose-800/40 to-zinc-900/40", testid: "world-horror" },
            { name: "Stand-Up Comedy", href: "/collection/stand-up-comedy", color: "from-amber-700/30 to-yellow-900/10", testid: "world-standup" },
            { name: "Animation", href: "/collection/animation", color: "from-sky-700/30 to-indigo-900/10", testid: "world-animation" },
            { name: "Corporate & Startups", href: "/collection/corporate-startup-stories", color: "from-slate-700/30 to-slate-900/10", testid: "world-corporate" },
            { name: "Editor's Picks ✨", href: "/list/editor-picks", color: "from-violet-700/30 to-violet-900/10", testid: "world-editor-picks" },
          ].map((w) => (
            <Link key={w.testid} to={w.href}
                  data-testid={w.testid}
                  className={`relative h-28 rounded-md overflow-hidden border border-white/10 bg-gradient-to-br ${w.color} flex items-end p-4 hover:border-white/30 transition-colors`}>
              <span className="font-display text-xl font-bold">{w.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Browse by Language */}
      <section className="px-6 lg:px-10 py-12 max-w-[1440px] mx-auto" data-testid="browse-language">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">Browse by Language</h2>
        <div className="flex flex-wrap gap-3">
          {LANGUAGE_TILES.map((l) => (
            <Link key={l.code} to={`/discover?language=${l.code}`}
                  data-testid={`lang-tile-${l.code}`}
                  className="px-5 py-2.5 rounded-full border border-white/10 hover:border-[#E50914] hover:text-white text-neutral-300 transition-colors font-medium text-sm">
              {l.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Support Us banner — subtle, ad-free messaging */}
      <section className="px-6 lg:px-10 mt-16 pb-4" data-testid="home-support-banner">
        <Link to="/support" className="block max-w-3xl mx-auto p-6 rounded-md border border-[#E50914]/30 bg-gradient-to-r from-[#E50914]/10 via-[#E50914]/[0.04] to-transparent hover:border-[#E50914]/60 transition-colors group">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#E50914]/20 border border-[#E50914]/40 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">💛</span>
            </div>
            <div className="flex-1">
              <h3 className="font-display text-base sm:text-lg font-bold tracking-tight group-hover:text-white text-neutral-200">
                Filmistani is ad-free & independent
              </h3>
              <p className="text-xs sm:text-sm text-neutral-500 mt-0.5">
                If we've helped you find your next great watch, support us with whatever feels right.
              </p>
            </div>
            <span className="text-[#E50914] text-sm font-semibold whitespace-nowrap group-hover:translate-x-1 transition-transform">
              Support →
            </span>
          </div>
        </Link>
      </section>

      {/* ── Published custom admin rails — sorted by admin drag order ── */}
      {customRailMeta.map(railDef => {
        const items = customRailData[railDef.key];
        if (!items?.length) return null;
        return (
          <section key={railDef.key} className="px-4 sm:px-6 lg:px-10 py-2">
            <MovieCarousel
              title={railDef.label}
              items={items}
              endpoint={`/collections/${railDef.key}`}
              showPlatform
            />
          </section>
        );
      })}

      {secVisible("social_widget") && (
        <LazyMount placeholderHeight={180} testid="lazy-social-widget">
          <HomeSocialWidget />
        </LazyMount>
      )}

      <footer className="px-6 lg:px-10 py-12 mt-12 border-t border-white/5 text-center">
        <p className="font-display text-sm text-neutral-500">
          Filmistani — Find great cinema faster.
        </p>
        <div className="mt-3 flex items-center justify-center gap-x-4 gap-y-2 flex-wrap text-xs text-neutral-600">
          <Link to="/support" className="hover:text-[#E50914] transition-colors" data-testid="footer-support-link">
            💛 Support Us
          </Link>
          <span className="text-neutral-700">·</span>
          <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">
            Contact Us
          </Link>
          <span className="text-neutral-700">·</span>
          <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">
            Privacy
          </Link>
          <span className="text-neutral-700">·</span>
          <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">
            Terms
          </Link>
          <span className="text-neutral-700">·</span>
          <Link to="/shipping-policy" className="hover:text-white transition-colors" data-testid="footer-shipping-link">
            Shipping
          </Link>
          <span className="text-neutral-700">·</span>
          <Link to="/cancellation-refunds" className="hover:text-white transition-colors" data-testid="footer-refunds-link">
            Cancellation &amp; Refunds
          </Link>
        </div>
        <p className="mt-4 text-[11px] text-neutral-600" data-testid="footer-built-by">
          Built by{" "}
          <a
            href="https://brandguru.online"
            target="_blank"
            rel="noopener noreferrer"
            className="text-neutral-400 hover:text-white transition-colors underline-offset-2 hover:underline"
          >
            BrandGuru
          </a>
          {" "}— a creative-tech studio.
        </p>
      </footer>
      <ScrollProgressChip />
    </div>
  );
}
